import React, { useMemo, useState, createElement } from 'react';
import { Download, Search, Calendar, FileText } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { paymentReconciliations } from '../../data/reportMockData';
export function PaymentReconciliationReport() {
  const [dateFrom, setDateFrom] = useState('2024-01-01');
  const [dateTo, setDateTo] = useState('2024-12-31');
  const [appliedFrom, setAppliedFrom] = useState('2024-01-01');
  const [appliedTo, setAppliedTo] = useState('2024-12-31');
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;
  const filtered = useMemo(() => {
    return paymentReconciliations.filter(r => r.date >= appliedFrom && r.date <= appliedTo);
  }, [appliedFrom, appliedTo]);
  const totalPages = Math.ceil(filtered.length / rowsPerPage);
  const paginated = filtered.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage);
  const handleSearch = () => {
    setAppliedFrom(dateFrom);
    setAppliedTo(dateTo);
    setCurrentPage(1);
  };
  const handleDownload = () => {
    const headers = ['Date', 'Payer Name', 'Payer Phone', 'Service Name', 'Transaction ID', 'Amount Collected', 'Collection Medium', 'Amount Reconciled', 'Reconciliation Medium', 'Reconciliation Date', 'Reference'];
    const rows = filtered.map(r => [r.date, r.payerName, r.payerPhone, r.serviceName, r.transactionId, r.amountCollected, r.collectionMedium, r.amountReconciled, r.reconciliationMedium, r.reconciliationDate, r.reference]);
    const csv = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csv], {
      type: 'text/csv'
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payment-reconciliation-${appliedFrom}-to-${appliedTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const mediumBadgeColor = (medium: string) => {
    if (medium === 'bKash') return 'bg-pink-100 text-pink-700';
    if (medium === 'Nagad') return 'bg-orange-100 text-orange-700';
    if (medium === 'Rocket') return 'bg-purple-100 text-purple-700';
    return 'bg-blue-100 text-blue-700';
  };
  return <div className="space-y-6" data-id="element-365">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-366">
        <div data-id="element-367">
          <h1 className="text-2xl font-bold text-slate-800" data-id="element-368">
            Payment Reconciliation Report
          </h1>
          <p className="text-slate-500" data-id="element-369">
            Track and verify payment reconciliation records
          </p>
        </div>
        <Button onClick={handleDownload} variant="secondary" data-id="element-370">
          <Download className="mr-2 h-4 w-4" data-id="element-371" /> Download CSV
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4" data-id="element-372">
        <div className="flex flex-wrap items-end gap-4" data-id="element-373">
          <div data-id="element-374">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-375">
              From Date
            </label>
            <div className="relative" data-id="element-376">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-377" />
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-10 pl-9 pr-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" data-id="element-378" />
            </div>
          </div>
          <div data-id="element-379">
            <label className="block text-xs font-medium text-slate-500 mb-1" data-id="element-380">
              To Date
            </label>
            <div className="relative" data-id="element-381">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" data-id="element-382" />
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-10 pl-9 pr-3 rounded-md border border-slate-300 text-sm focus:ring-2 focus:ring-emerald-500 focus:outline-none" data-id="element-383" />
            </div>
          </div>
          <Button onClick={handleSearch} data-id="element-384">
            <Search className="mr-2 h-4 w-4" data-id="element-385" /> Search
          </Button>
          <div className="ml-auto text-sm text-slate-500 self-center" data-id="element-386">
            <span className="font-semibold text-slate-800" data-id="element-387">
              {filtered.length}
            </span>{' '}
            records found
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-id="element-388">
        <div className="overflow-x-auto" data-id="element-389">
          <table className="w-full text-sm text-left" data-id="element-390">
            <thead className="bg-slate-50 border-b border-slate-200" data-id="element-391">
              <tr data-id="element-392">
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-393">
                  Date
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-394">
                  Payer Name
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-395">
                  Phone
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-396">
                  Service
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-397">
                  Transaction ID
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-emerald-600 uppercase tracking-wider whitespace-nowrap bg-emerald-50" data-id="element-398">
                  Collected (৳)
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-emerald-600 uppercase tracking-wider whitespace-nowrap bg-emerald-50" data-id="element-399">
                  Collection Medium
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-blue-600 uppercase tracking-wider whitespace-nowrap bg-blue-50" data-id="element-400">
                  Reconciled (৳)
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-blue-600 uppercase tracking-wider whitespace-nowrap bg-blue-50" data-id="element-401">
                  Reconciliation Medium
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-blue-600 uppercase tracking-wider whitespace-nowrap bg-blue-50" data-id="element-402">
                  Reconciliation Date
                </th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap" data-id="element-403">
                  Reference
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-404">
              {paginated.length > 0 ? paginated.map(row => <tr key={row.id} className="hover:bg-slate-50 transition-colors" data-id="element-405">
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-id="element-406">
                      {row.date}
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-900 whitespace-nowrap" data-id="element-407">
                      {row.payerName}
                    </td>
                    <td className="px-4 py-3 text-slate-600 whitespace-nowrap" data-id="element-408">
                      {row.payerPhone}
                    </td>
                    <td className="px-4 py-3" data-id="element-409">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${row.serviceName === 'Khatian' ? 'bg-teal-100 text-teal-700' : 'bg-amber-100 text-amber-700'}`} data-id="element-410">
                        {row.serviceName}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap" data-id="element-411">
                      {row.transactionId}
                    </td>
                    <td className="px-4 py-3 bg-emerald-50/50 font-semibold text-emerald-800" data-id="element-412">
                      ৳{row.amountCollected}
                    </td>
                    <td className="px-4 py-3 bg-emerald-50/50" data-id="element-413">
                      <span className="bg-violet-100 text-violet-700 text-xs font-medium px-2 py-0.5 rounded-full" data-id="element-414">
                        {row.collectionMedium}
                      </span>
                    </td>
                    <td className="px-4 py-3 bg-blue-50/50 font-semibold text-blue-800" data-id="element-415">
                      ৳{row.amountReconciled}
                    </td>
                    <td className="px-4 py-3 bg-blue-50/50" data-id="element-416">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${mediumBadgeColor(row.reconciliationMedium)}`} data-id="element-417">
                        {row.reconciliationMedium}
                      </span>
                    </td>
                    <td className="px-4 py-3 bg-blue-50/50 text-slate-600 whitespace-nowrap" data-id="element-418">
                      {row.reconciliationDate}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-600 whitespace-nowrap" data-id="element-419">
                      {row.reference}
                    </td>
                  </tr>) : <tr data-id="element-420">
                  <td colSpan={11} className="px-4 py-12 text-center text-slate-400" data-id="element-421">
                    <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" data-id="element-422" />
                    No records found for the selected date range.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-4 py-3 border-t border-slate-200 bg-slate-50 flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-slate-500" data-id="element-423">
          <span data-id="element-424">
            Showing{' '}
            {Math.min((currentPage - 1) * rowsPerPage + 1, filtered.length)}–
            {Math.min(currentPage * rowsPerPage, filtered.length)} of{' '}
            {filtered.length}
          </span>
          <div className="flex items-center space-x-1" data-id="element-425">
            <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1} className="px-3 py-1 rounded border border-slate-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-426">
              Previous
            </button>
            {Array.from({
            length: Math.min(5, totalPages)
          }, (_, i) => {
            const page = i + 1;
            return <button key={page} onClick={() => setCurrentPage(page)} className={`px-3 py-1 rounded border transition-colors ${currentPage === page ? 'bg-emerald-600 text-white border-emerald-600' : 'border-slate-300 hover:bg-white'}`} data-id="element-427">
                    {page}
                  </button>;
          })}
            <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages || totalPages === 0} className="px-3 py-1 rounded border border-slate-300 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed transition-colors" data-id="element-428">
              Next
            </button>
          </div>
        </div>
      </div>
    </div>;
}